import React, { useState } from 'react';

// Hooks
import useRecoverCustomer from '@src/hooks/useCustomerRecover';

// Comps
import { TextField } from "@src/stories/elements/TextField/TextField";
import { Link } from '@arctheme-components/elements/Link/Link';
import { Paragraph } from '@arctheme-components/elements/Paragraph/Paragraph';
import ButtonSpinner from '@src/stories/elements/ButtonSpinner/ButtonSpinner';

type Props = {
    onCancel: (event: React.MouseEvent<HTMLAnchorElement>) => void;
    section: any;
    blockSettings?: any;
};

const AccountForgotPassword = ({ onCancel, section, blockSettings }: Props) => {
    const forgotPasswordSettings = blockSettings?.forgotPassword || {};
    const {
        title_forgot_password = forgotPasswordSettings?.title_forgot_password || "Forgot password?",
        title_forgot_password_style = forgotPasswordSettings?.title_forgot_password_style || "h3",
        title_forgot_password_color = forgotPasswordSettings?.title_forgot_password_color || "#000000",
        tagline_forgot_password = forgotPasswordSettings?.tagline_forgot_password || "No worries! We can help you with resetting your password!",
        email_label = forgotPasswordSettings?.email_label || "Email address",
        email_placeholder = forgotPasswordSettings?.email_placeholder || "Email address",
        button_label_forgot_password_submit = forgotPasswordSettings?.button_label_forgot_password_submit || "Submit",
        button_style_forgot_password_submit = forgotPasswordSettings?.button_style_forgot_password_submit || "primary",
        button_label_cancel = forgotPasswordSettings?.button_label_cancel || "Cancel Request"
    } = section;

    const [email, setEmail] = useState('');

    const { recoverCustomer, loading, errorMessage, successMessage } = useRecoverCustomer();

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        await recoverCustomer(email);
    };

    return (
        <form
            id='forgotForm'
            onSubmit={handleSubmit}
            className='account-page__form'
        >
            <div className='flex flex-col gap-y-4'>
                <p 
                    className={title_forgot_password_style}
                    style={{ color: title_forgot_password_color }}
                >
                    {title_forgot_password}
                </p>

                <p className='b4'>
                    {tagline_forgot_password}
                </p>

                <TextField
                    className='w-full'
                    inputType='email'
                    type="floating-input" 
                    id='emailForgot'
                    name='email'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required={true}
                    label={`${email_label}*`}
                    placeholder={email_placeholder}
                />

                {errorMessage && (
                    <Paragraph
                        text={errorMessage}
                        type='errors'
                        maxChars={9999}
                        classes='text-rhv-red b4'
                    />
                )}

                {successMessage && (
                    <Paragraph
                        text={successMessage}
                        type='b4'
                        maxChars={9999}
                        classes='text-rhv-green b4'
                    />
                )}

                <div className='flex flex-col md:flex-row gap-y-4 justify-center md:gap-4 mt-4'>
                    <ButtonSpinner
                        isLoading={loading}
                        classes={`button button-${button_style_forgot_password_submit} w-full md:w-auto flex-1 min-h-[60px] !text-lg block !mt-0`}
                        ariaLabel='Submit recover password form'
                        loadingText={'Submitting...'}
                        disabled={successMessage}
                    >
                        {button_label_forgot_password_submit}
                    </ButtonSpinner>

                    <Link
                        link='#'
                        onClick={onCancel}
                        classes='button button-secondary w-full md:w-auto flex-1 min-h-[60px] !text-lg block'
                        ariaLabel='Cancel password reset'
                    >
                        {button_label_cancel}
                    </Link>
                </div>
            </div>
        </form>
    );
};

export default AccountForgotPassword;